import { CheckCircle2, ShieldAlert, ShieldOff, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useEscalateAlert, useUpdateAlert } from '@/api/hooks/useAlertDetail';
import type { AlertDetailResponse, AlertStatus } from '@/types/alerts';

interface DispositionAction {
  label: string;
  status: AlertStatus;
  /** Resolution-notes label persisted alongside the status change. */
  noteLabel: string;
  variant: 'default' | 'destructive' | 'secondary' | 'outline';
  icon: React.ComponentType<{ className?: string }>;
  /** Use the /escalate endpoint rather than PATCH. */
  escalate?: boolean;
}

const ACTIONS: DispositionAction[] = [
  {
    label: 'Confirmed Fraud',
    status: 'Resolved',
    noteLabel: 'confirmed_fraud',
    variant: 'destructive',
    icon: ShieldAlert,
  },
  {
    label: 'Legitimate Variation',
    status: 'Dismissed',
    noteLabel: 'legitimate_variation',
    variant: 'secondary',
    icon: CheckCircle2,
  },
  {
    label: 'Data Error',
    status: 'Dismissed',
    noteLabel: 'data_error',
    variant: 'outline',
    icon: ShieldOff,
  },
  {
    label: 'Escalate',
    status: 'Escalated',
    noteLabel: 'escalated',
    variant: 'default',
    icon: TriangleAlert,
    escalate: true,
  },
];

interface DispositionSectionProps {
  alert: AlertDetailResponse;
}

/**
 * Section 8: investigator decision panel. Lifted unchanged from the
 * previous monolithic AlertDetail.tsx — same useUpdateAlert /
 * useEscalateAlert hooks, same noteLabel persistence convention, same
 * mutation invalidation behaviour (which keeps the Fraud Center list in
 * sync after a disposition).
 *
 * Recommended-action copy renders above the buttons so investigators see
 * the rule's suggested next step before clicking.
 */
export function DispositionSection({ alert }: DispositionSectionProps): JSX.Element {
  const updateAlert = useUpdateAlert(alert.id);
  const escalateAlert = useEscalateAlert(alert.id);

  const handle = (action: DispositionAction) => {
    if (action.escalate) {
      escalateAlert.mutate({ reason: action.noteLabel });
      return;
    }
    updateAlert.mutate({
      status: action.status,
      resolution_notes: `[disposition] ${action.noteLabel}`,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Disposition</CardTitle>
        <CardDescription>
          Record an outcome. This writes to <code>fraud.alert.status</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h4 className="text-xs font-semibold uppercase text-muted-foreground">
            Recommended action
          </h4>
          <p className="mt-1 text-sm">{alert.recommended_action ?? 'None.'}</p>
        </div>
        <Separator />
        <div className="flex flex-wrap gap-2">
          {ACTIONS.map((action) => {
            const Icon = action.icon;
            const pending =
              (action.escalate && escalateAlert.isPending) ||
              (!action.escalate && updateAlert.isPending);
            return (
              <Button
                key={action.label}
                variant={action.variant}
                onClick={() => handle(action)}
                disabled={pending}
              >
                <Icon className="mr-2 h-4 w-4" />
                {action.label}
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
